import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { getDocument } from '@atlaskit/browser-apis';
import Button, { IconButton } from '@atlaskit/button/new';
import { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import CopyIcon from '@atlaskit/icon/core/copy';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import { ACCEPTABLE_USE_VIOLATIONS_ERROR } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import { useStoreUserMessagesPreference } from '@atlassian/conversation-assistant-store/controllers/store-user-messages-be';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { PaperErrorMessage } from '@atlassian/conversation-assistant-ui-components/ui/Paper';
import { APPROVAL_REQUIRED_FOR_FIRST_PUBLISH } from '@atlassian/rovo-platform-ui-components/confluence-object-finalize-popup/approval-required-error-token';

import { MessageFooterButtons } from '../../../common/ui/message-footer-buttons';
import { AUPFeedbackBanner } from '../../../common/ui/message-footer-buttons/aup-feedback-banner';
import { AUP_POSITIVE_CATEGORIES } from '../../../common/ui/message-footer-buttons/feedback-confirmation-banner/AUP_POSITIVE_CATEGORIES';
import { FeedbackConfirmationBanner } from '../../../common/ui/message-footer-buttons/feedback-confirmation-banner/FeedbackConfirmationBanner';
import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useAssistanceServiceParams } from '../../../common/utils/use-assistance-service-params';
import { useConversation } from '../../../controllers/conversation/useConversation';
import { useFeedbackCollectorActions } from '../../../controllers/feedback-collector/useFeedbackCollectorActions';
import { useFeedbackV2 } from '../../../controllers/feedback-v2';
import { useFeedbackTrigger } from '../../../controllers/feedback-v2/use-feedback-trigger';
import { useMessageHistory } from '../../../controllers/message-history';
import { type WithInteractionMode } from '../interaction-mode';

import messages from './messages';

type StoreErrorViewCommonProps = {
	error: StoreError;
};

type LiveStoreErrorViewProps = StoreErrorViewCommonProps & {
	onResendMessage?: () => void;
};

type ReadOnlyStoreErrorViewProps = StoreErrorViewCommonProps & {
	onResendMessage?: never;
};

type StoreErrorViewProps = WithInteractionMode<
	LiveStoreErrorViewProps,
	ReadOnlyStoreErrorViewProps
>;

const TraceIdViewer = ({ traceId }: { traceId: string }) => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'rovoErrorTraceIdViewer',
		});
	}, [sendAnalyticsEvent]);

	return (
		<Inline alignBlock="center" space="space.050">
			<Text size="small" weight="semibold">
				{formatMessage(messages.traceIdMessage, {
					traceId,
				})}
			</Text>

			<IconButton
				label={formatMessage(messages.copyTraceId)}
				appearance="subtle"
				icon={CopyIcon}
				onClick={(_, event) => {
					sendAnalyticsEvent(event.update({ actionSubjectId: 'copyTraceIdButton' }));
					navigator.clipboard.writeText(traceId);
				}}
			/>
		</Inline>
	);
};

// Known client-side fetch error message patterns (browser-generated, never reach Atlassian servers)
const CLIENT_FETCH_ERROR_PATTERNS = [
	'Failed to fetch',
	'Load failed',
	'NetworkError',
	'Network request failed',
	'fetch is not defined',
];

const getErrorMessage = (
	content: string,
	status_code: number | undefined,
	message_template: string | undefined,
	errorType: string | undefined,
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string => {
	if (errorType === 'RovoChatStreamTimeoutError') {
		return formatMessage(messages.chatStreamTimeoutError);
	}

	if (CLIENT_FETCH_ERROR_PATTERNS.some((pattern) => content.includes(pattern))) {
		return formatMessage(messages.clientNetworkErrorMessage);
	}

	if (message_template === ASSISTANCE_SERVICE_ERROR_CODES.ROVO_PRODUCT_NOT_ACTIVE) {
		return formatMessage(messages.productNotActiveErrorMessage);
	}

	if (status_code === 401) {
		return formatMessage(messages.authErrorMessage);
	}

	if (status_code === 403) {
		return formatMessage(messages.permissionErrorMessage);
	}

	if (content.includes('network error')) {
		return formatMessage(messages.networkErrorMessage);
	}

	// CCIMPORTER-3221: when the destination Confluence space requires approval
	// before publishing new pages for the first time, the convo-ai BE surfaces
	// HTTP 409 + the APPROVAL_REQUIRED_FOR_FIRST_PUBLISH token in the response
	// body (see CCIMPORTER-3249). The actionable copy remains controlled by
	// confluence_approvals_eap (experiment, isEnabled=true).
	if (status_code === 409 && content.includes(APPROVAL_REQUIRED_FOR_FIRST_PUBLISH)) {
		// TODO CCIMPORTER-3221: register `confluence_approvals_eap` in
		// `@atlaskit/tmp-editor-statsig` (`experiments-config.ts`) so the
		// experiment id is known to TypeScript and this cast can be
		// removed. The experiment exists on the backend (CCIMPORTER-2017).
		if (
			expValEqualsNoExposure(
				'confluence_approvals_eap' as Parameters<typeof expValEqualsNoExposure>[0],
				'isEnabled',
				true,
			)
		) {
			return formatMessage(messages.approvalRequiredForFirstPublishErrorMessage);
		}
	}

	if (status_code !== undefined && status_code >= 500) {
		return formatMessage(messages.serverErrorMessage);
	}

	return content;
};

const LiveStoreErrorView = ({
	error: { id, content, traceId, message_template, status_code, errorType },
	onResendMessage,
}: LiveStoreErrorViewProps): React.JSX.Element => {
	di(fg);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const {
		profileData: { aaid, name, email },
		feedbackCollector: { entryPointId },
		productKey,
		assistanceServiceConfig,
		siteId,
		experienceId,
		productHeader,
	} = useGlobalConfig();
	const { selectedAgent, conversation } = useConversation();
	const feedbackTrigger = useFeedbackTrigger();
	const messageContent = getErrorMessage(
		content,
		status_code,
		message_template,
		errorType,
		formatMessage,
	);

	const [displayFlag, setDisplayFlag] = useState(false);

	const feedbackActions = useFeedbackCollectorActions();
	const assistanceServiceParams = useAssistanceServiceParams({ touchpointSource: 'error-message' });

	const {
		preferences: { hasSeenFeedbackOptInPrompt },
		setHasSeenFeedbackOptInPrompt,
	} = useUserPreferences();
	const { storeUserMessages, setStoreUserMessages } = useStoreUserMessagesPreference({
		assistanceServiceParams,
	});

	const isAUPError = message_template === ACCEPTABLE_USE_VIOLATIONS_ERROR;

	const showResendMessage = !!onResendMessage;

	const history = useMessageHistory({
		canAttachMessages: true,
		answerId: id,
	});
	const thumbTriggerRef = useRef<HTMLElement | null>(null);

	const {
		feedbackType: feedbackTypeV2,
		isBannerVisible,
		shouldHideCheckboxes,
		handleThumbClick: handleV2ThumbClick,
		handleProvideAdditionalFeedback,
		handleFeedbackSubmit,
		handleDismissBanner,
		handleIncludeMessagesChecked,
		handleOptIn,
		isOptedIn,
		isAUPThumbsDownBannerVisible,
		showAUPThumbsDownBanner,
		hideAUPThumbsDownBanner,
		handleAUPIncorrectFlagSubmit,
	} = useFeedbackV2({
		entryPointId,
		aaid: aaid ?? 'unknown',
		name,
		email,
		productKey,
		agentMessageId: id,
		conversationId: conversation?.id,
		history,
		traceId,
		isAUPError,
		minionAlias: assistanceServiceConfig?.minionAlias,
		channelId: assistanceServiceConfig?.channelId,
		storeUserMessages,
		siteId,
		experienceId,
		productHeader,
		setStoreUserMessages,
		feedbackTrigger,
	});

	const onThumbClick = (isLike: boolean) => {
		thumbTriggerRef.current = (getDocument()?.activeElement ?? null) as HTMLElement | null;

		if (isAUPError) {
			if (isLike) {
				hideAUPThumbsDownBanner();
				handleV2ThumbClick(true);
			} else {
				handleV2ThumbClick(false);
				showAUPThumbsDownBanner();
			}
			return;
		}

		feedbackActions.open({
			answerQuality: isLike ? 'good' : 'bad',
			feedbackSource: 'errorMessageFeedback',
		});

		trackAIMAUEvent({
			type: 'feedback',
			feedbackResult: isLike ? 'up' : 'down',
			proactive: false,
			singleInstrumentationID: id,
		});
	};

	const onCopyClick = useCallback(() => {
		navigator.clipboard.writeText(messageContent.toString());

		trackAIMAUEvent({
			type: 'actioned',
			actionResult: 'copyButtonClicked',
			proactive: false,
			singleInstrumentationID: id,
		});
	}, [messageContent, trackAIMAUEvent, id]);

	const onClickInsertReplace = useCallback(
		(mode: 'insert' | 'replace') => {
			trackAIMAUEvent({
				type: 'actioned',
				actionResult: mode,
				subjectId: 'insertReplaceButton',
				proactive: false,
				singleInstrumentationID: id,
			});
		},
		[id, trackAIMAUEvent],
	);

	const aupLink = (
		<Link href="https://www.atlassian.com/legal/acceptable-use-policy">
			{formatMessage(messages.aupLinkText)}
		</Link>
	);

	const aupErrorMessage = (
		<Stack space="space.050">
			<Text>{formatMessage(messages.aupErrorMessagePartOne)} </Text>
			<Text>{formatMessage(messages.aupErrorMessagePartTwo, { link: aupLink })}</Text>
		</Stack>
	);

	return (
		<>
			<PaperErrorMessage
				hideIcon={isAUPError}
				message={isAUPError ? aupErrorMessage : messageContent}
				variant="neutral"
				pinchCorner="bottomLeft"
				contentUnder={
					<Stack space="space.100">
						{!!traceId && fg('rovo_chat_show_error_trace_id') && (
							<TraceIdViewer traceId={traceId} />
						)}
						{showResendMessage && !isAUPError && (
							<Inline>
								<Button
									onClick={(_, event) => {
										sendAnalyticsEvent(event.update({ actionSubjectId: 'resendMessageButton' }));
										onResendMessage();
									}}
								>
									{formatMessage(messages.resend)}
								</Button>
							</Inline>
						)}
						<MessageFooterButtons
							hideThumbs={false}
							onThumbClick={onThumbClick}
							onCopyClick={onCopyClick}
							insertReplaceContent={messageContent}
							selectedAgentId={selectedAgent?.id}
							onClickInsertReplace={onClickInsertReplace}
							feedbackType={isAUPError ? feedbackTypeV2 : undefined}
						/>
						{isAUPError && feedbackTypeV2 === 'like' && (
							<FeedbackConfirmationBanner
								isVisible={isBannerVisible}
								feedbackType="like"
								onProvideAdditionalFeedback={handleProvideAdditionalFeedback}
								onDismiss={() => {
									handleDismissBanner();
									thumbTriggerRef.current?.focus();
								}}
								onSubmit={handleFeedbackSubmit}
								onIncludeMessagesChecked={handleIncludeMessagesChecked}
								customCategories={AUP_POSITIVE_CATEGORIES}
								onOptIn={handleOptIn}
								isOptedIn={isOptedIn}
								hasSeenFeedbackOptInPrompt={hasSeenFeedbackOptInPrompt}
								onOptInPromptActioned={
									setHasSeenFeedbackOptInPrompt
										? () => setHasSeenFeedbackOptInPrompt(true)
										: undefined
								}
							/>
						)}
						{isAUPError && isAUPThumbsDownBannerVisible && (
							<AUPFeedbackBanner
								isVisible={isAUPThumbsDownBannerVisible}
								onSubmit={handleAUPIncorrectFlagSubmit}
								onDismiss={() => {
									hideAUPThumbsDownBanner();
									thumbTriggerRef.current?.focus();
								}}
								userEmail={email}
								shouldHideCheckboxes={shouldHideCheckboxes}
							/>
						)}
					</Stack>
				}
			/>

			{displayFlag && (
				<FlagGroup onDismissed={() => setDisplayFlag(false)}>
					<FeedbackFlag />
				</FlagGroup>
			)}
		</>
	);
};

export const StoreErrorView = (props: StoreErrorViewProps): React.JSX.Element | null => {
	if (props.interactionMode === 'read-only') {
		return null;
	}

	return <LiveStoreErrorView error={props.error} onResendMessage={props.onResendMessage} />;
};
